<template>
  <div class="user-actions">
    <AlertsSnackbar :is-blocked="isAlertReviewOpen" />
    <AlertBell class="user-actions__alert-bell" @click="openAlerts" />
    <AlertsDrawer v-model="isAlertsDrawerOpen" />
    <AlertReviewModal v-model="isAlertReviewOpen" />
    <button
      class="user-actions__toggle-dropdown"
      :class="{ 'hide-on-mobile-app': !isAvailableSettings }"
      :aria-label="`Open ${buttonText} menu`"
      @click.stop="toggleDropdown"
    >
      <span class="user-actions__title" data-hj-suppress>{{ buttonText }}</span>
      <img class="user-actions__title_icon" src="@/assets/icons/user.svg" alt="user" />
    </button>
    <!-- This handler only stops clicks bubbling; actions are native controls inside the menu. -->
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
    <div v-if="isVisibleDropdown" class="user-actions__dropdown" @click.stop>
      <div class="user-actions__dropdown-title" data-hj-suppress>{{ menuTitle }}</div>
      <!-- Clicks are delegated from native links and buttons; the list itself is not an action. -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <ul class="user-actions__dropdown-actions" @click="hideDropdown">
        <li v-if="isAvailableSettings" class="user-actions__list-item">
          <router-link class="user-actions__link" :to="{ name: 'Settings' }">Settings</router-link>
        </li>
        <li v-if="isAvailableDeleteAccount" class="user-actions__list-item hide-on-mobile-app">
          <router-link class="user-actions__link" :to="{ name: 'DeleteAccount' }"
            >Delete account</router-link
          >
        </li>
        <li class="user-actions__list-item hide-on-mobile-app">
          <BaseButton
            style-type="link"
            class="user-actions__link button-link"
            aria-label="Logout"
            @click="$emit('logout')"
            >Logout</BaseButton
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import AlertBell from '@/components/alert-bell/AlertBell.vue';
  import AlertsDrawer from '@/components/alerts-drawer/AlertsDrawer.vue';
  import AlertsSnackbar from '@/components/alerts-snackbar/AlertsSnackbar.vue';
  import BaseButton from '@/components/base/BaseButton.vue';
  import AlertReviewModal from '@/components/modals/alerts-review-modal/AlertReviewModal.vue';
  import useDropdownVisibility from '@/hooks/useDropdownVisibility';
  import { useCreditReport, useOutcomeStore } from '@/stores';
  import { PORTAL_TYPE } from '@/types/enums';

  const emit = defineEmits<{
    (n: 'logout'): void
  }>();

  const route = useRoute();
  const outcomeStore = useOutcomeStore();
  const creditReportStore = useCreditReport();

  const { isVisibleDropdown, toggleDropdown, hideDropdown } = useDropdownVisibility();

  const isAlertsDrawerOpen = ref(false);
  const isAlertReviewOpen = ref(false);

  const isAvailableDeleteAccount = computed<boolean>(
    () => route.meta?.portalType === PORTAL_TYPE.PRE_PORTAL,
  );
  const isAvailableSettings = computed<boolean>(
    () =>
      outcomeStore.isPositive &&
      !outcomeStore.isReconfirmationRequired &&
      route.name !== 'Settings',
  );
  const buttonText = computed<string>(() => creditReportStore.nameParts.forename || 'Settings');
  const menuTitle = computed<string>(() => {
    if (creditReportStore.nameParts.forename && creditReportStore.nameParts.surname)
      return `${creditReportStore.nameParts.forename} ${creditReportStore.nameParts.surname}`;
    return 'Settings';
  });

  const logout = () => {
    emit('logout');
  };

  const openAlerts = () => {
    if (window.matchMedia('(min-width: 760px)').matches) {
      isAlertsDrawerOpen.value = true;
      isAlertReviewOpen.value = false;
      return;
    }

    isAlertReviewOpen.value = true;
    isAlertsDrawerOpen.value = false;
  };

  defineExpose({
    isVisibleDropdown,
    isAvailableDeleteAccount,
    isAvailableSettings,
    isAlertsDrawerOpen,
    isAlertReviewOpen,
    buttonText,
    menuTitle,
    logout,
    openAlerts,
  });
</script>

<style scoped lang="scss">
  .user-actions {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 0 auto;

    &__toggle-dropdown {
      position: relative;
      padding-right: 15px;
      background-color: transparent;

      @include new-ms {
        &:after {
          position: absolute;
          top: 50%;
          right: 0;
          width: 9px;
          height: 19px;
          content: '';
          background-color: transparent;
          background-image: url('@/assets/icons/dropdown-arrow-blue.svg');
          background-repeat: no-repeat;
          transform: translate(0, -50%);
        }
      }
    }

    &__title {
      display: none;
      font-size: remc(12px);
      font-weight: 600;
      line-height: remc(24px);
      color: rgba(33, 78, 163, 1);

      @include new-ms {
        display: block;
      }

      &_icon {
        display: block;

        @include new-ms {
          display: none;
        }
      }
    }

    &__dropdown {
      position: absolute;
      top: 145%;
      right: 0;
      z-index: 5;
      width: 190px;
      background: $brand-primary-100-white;
      border-radius: 2px;

      @include new-md {
        top: 80%;
        right: 0;
        left: auto;

        &::after {
          right: 10px;
          left: auto;
        }
      }
    }

    &__dropdown-title {
      padding: 14px 20px;
      font-size: remc(15px);
      font-weight: 900;
      line-height: remc(24px);
      border-bottom: 1px solid $white-3;
    }

    &__dropdown-actions {
      padding: 7px 20px;
      margin-bottom: 0;
    }

    &__list-item {
      padding: 7px 0;
    }

    &__link {
      justify-content: flex-start;
      font-size: remc(12px);

      &.button-link {
        width: max-content;
        text-align: left;

        :deep(.base-button__content) {
          font-size: remc(12px);
        }
      }
    }
  }
</style>
